import React from 'react';
import {motion} from 'framer-motion';
import {LineChart,Line,ResponsiveContainer} from 'recharts';
import useDashboardStore from '../store/dashboardStore';
import {formatCurrency,formatPercent,formatDelta} from '../utils/formatters';

const formatDateRange=(dateFrom,dateTo)=> {
  if (dateFrom===dateTo) {
    return new Date(dateFrom).toLocaleDateString('en-US',{
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  }

  const startDate=new Date(dateFrom);
  const endDate=new Date(dateTo);

  // If same year,show abbreviated format
  if (startDate.getFullYear()===endDate.getFullYear()) {
    if (startDate.getMonth()===endDate.getMonth()) {
      // Same month and year
      return `${startDate.toLocaleDateString('en-US',{month: 'short'})} ${startDate.getDate()}-${endDate.getDate()},${startDate.getFullYear()}`;
    } else {
      // Different months,same year
      return `${startDate.toLocaleDateString('en-US',{month: 'short',day: 'numeric'})} - ${endDate.toLocaleDateString('en-US',{month: 'short',day: 'numeric'})},${startDate.getFullYear()}`;
    }
  } else {
    // Different years
    return `${startDate.toLocaleDateString('en-US',{month: 'short',day: 'numeric',year: 'numeric'})} - ${endDate.toLocaleDateString('en-US',{month: 'short',day: 'numeric',year: 'numeric'})}`;
  }
};

const KPITile=({title,subtitle,value,delta,deltaPercent,sparklineData,isSelected,onClick,monochrome,isEmpty,isPercent=false})=> {
  // Enhanced delta info with proper color coding and inline styles
  const getDeltaInfo = () => {
    if (delta === null || delta === undefined) return null;
    
    const isPositive = delta > 0;
    const isNegative = delta < 0;
    const isUnchanged = delta === 0;
    
    let color;
    let symbol;
    
    if (monochrome) {
      color = '#BFBFBF'; // brand-muted color
      symbol = isPositive ? '▲' : isNegative ? '▼' : '●';
    } else {
      if (isPositive) {
        color = '#27AE60'; // Green color
        symbol = '▲';
      } else if (isNegative) {
        color = '#E74C3C'; // Red color  
        symbol = '▼';
      } else {
        color = '#C9A227'; // Gold/yellow color
        symbol = '●';
      }
    }
    
    return {
      symbol,
      color,
      value: formatCurrency(Math.abs(delta)),
      percent: formatPercent(Math.abs(deltaPercent || 0))
    };
  };

  const deltaInfo = getDeltaInfo();

  return (
    <motion.div
      onClick={onClick}
      className={`bg-brand-card rounded-card p-6 shadow-card cursor-pointer transition-all duration-200 ${
        isSelected ? 'ring-2 ring-brand-accent' : 'hover:bg-brand-card/80'
      } ${isEmpty ? 'opacity-60' : ''}`}
      whileHover={{scale: 1.02}}
      whileTap={{scale: 0.98}}
      initial={{opacity: 0,y: 20}}
      animate={{opacity: 1,y: 0}}
      transition={{duration: 0.16}}
    >
      <div className="flex justify-between items-start mb-4">
        <div className="flex-1 min-w-0">
          <p className="text-brand-muted text-sm uppercase tracking-wide font-medium truncate">
            {title}
          </p>
          <p className="text-brand-muted2 text-xs mt-1 truncate" title={subtitle}>
            {subtitle}
          </p>
          <p className="text-kpi font-bold text-brand-fg mt-2">
            {isEmpty ? '—' : isPercent ? formatPercent(value) : formatCurrency(value)}
          </p>
        </div>
        <div className="w-16 h-8 ml-3 flex-shrink-0">
          {!isEmpty && sparklineData?.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={sparklineData}>
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke={monochrome ? "#BFBFBF" : "#C9A227"}
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="w-full h-full bg-brand-muted2/20 rounded flex items-center justify-center">
              <span className="text-brand-muted2 text-xs">No data</span>
            </div>
          )}
        </div>
      </div>

      {!isEmpty && deltaInfo ? (
        <div className="flex items-center space-x-2">
          <span 
            className="text-sm font-semibold" 
            style={{color: deltaInfo.color}}
          >
            {deltaInfo.symbol}
          </span>
          <span 
            className="text-sm font-medium" 
            style={{color: deltaInfo.color}}
          >
            {deltaInfo.value}
          </span>
          <span 
            className="text-sm" 
            style={{color: deltaInfo.color}}
          >
            ({deltaInfo.percent})
          </span>
        </div>
      ) : (
        <div className="flex items-center space-x-2 text-brand-muted2">
          <span className="text-sm">—</span>
          <span className="text-sm">No comparison data</span>
        </div>
      )}
    </motion.div>
  );
};

const KPIGrid=()=> {
  const {
    kpis,
    primaryMetric,
    setPrimaryMetric,
    monochrome,
    seriesData,
    hasData,
    dateFrom,
    dateTo
  }=useDashboardStore();

  if (!kpis) return null;

  const sparklineData=hasData && seriesData?.length > 0 
    ? seriesData.slice(-7).map((d,i)=> ({value: d.revenue})) 
    : [];

  const dateRangeText=formatDateRange(dateFrom,dateTo);

  const tiles=[
    {
      key: 'revenue',
      title: 'Net Revenue',
      subtitle: dateRangeText,
      value: kpis.revenue.value,
      delta: kpis.revenue.delta,
      deltaPercent: kpis.revenue.deltaPercent,
      isPercent: false
    },
    {
      key: 'cost',
      title: 'Cost (COGS)',
      subtitle: dateRangeText,
      value: kpis.cost.value,
      delta: kpis.cost.delta,
      deltaPercent: kpis.cost.deltaPercent,
      isPercent: false
    },
    {
      key: 'profit',
      title: 'Profit (GP)',
      subtitle: dateRangeText,
      value: kpis.profit.value,
      delta: kpis.profit.delta,
      deltaPercent: kpis.profit.deltaPercent,
      isPercent: false
    },
    {
      key: 'discount',
      title: 'Discount',
      subtitle: dateRangeText,
      value: kpis.discount.value,
      delta: kpis.discount.delta,
      deltaPercent: kpis.discount.deltaPercent,
      isPercent: false
    },
    {
      key: 'profitRatio',
      title: 'Profit Ratio',
      subtitle: dateRangeText,
      value: kpis.profitRatio.value,
      delta: kpis.profitRatio.delta,
      deltaPercent: kpis.profitRatio.deltaPercent,
      isPercent: true
    }
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 px-6 py-4">
      {tiles.map((tile,index)=> (
        <motion.div
          key={tile.key}
          initial={{opacity: 0,y: 20}}
          animate={{opacity: 1,y: 0}}
          transition={{duration: 0.16,delay: index * 0.05}}
        >
          <KPITile
            title={tile.title}
            subtitle={tile.subtitle}
            value={tile.value}
            delta={tile.delta}
            deltaPercent={tile.deltaPercent}
            sparklineData={sparklineData}
            isSelected={primaryMetric===tile.key}
            onClick={()=> setPrimaryMetric(tile.key)}
            monochrome={monochrome}
            isEmpty={!hasData}
            isPercent={tile.isPercent}
          />
        </motion.div>
      ))}
    </div>
  );
};

export default KPIGrid;